/**
 * Named job-pool resolvers.
 *
 * These are the *only* functions the builder accepts on a prompt
 * node's `jobPool` field. The builder pattern-matches them by
 * function identity (`WeakMap`) and rewrites them to descriptor
 * objects (`{ kind: 'resolver', resolverId: '...' }`) before posting.
 * Loom evaluates the descriptor server-side; it never `eval`s code
 * from a consuming service.
 *
 * Bespoke `(ctx) => ...` functions in a workflow definition fail the
 * build with `WorkflowBuilderError`.
 */

import type { JobPoolRef, SessionContext, SerialisedJobPoolRef } from '../types';

const RESOLVER_DESCRIPTORS = new WeakMap<object, SerialisedJobPoolRef>();

/**
 * Resolves to `ctx.principal.jobPoolId` at session-activation
 * time. Throws `LoomResolverError` server-side if the consuming
 * service has not populated `principal.jobPoolId` (see
 * `sifter-provisioning.md` for the recipe).
 */
export const principalJobPool: (ctx: SessionContext) => string = makeNamedResolver(
  'principal.job_pool_id',
);

/**
 * Returns a fixed pool id or name at session-activation time. Use
 * sparingly; per-principal pools require `principalJobPool`.
 */
export function literalJobPool(idOrName: string): (ctx: SessionContext) => string {
  const fn = ((_ctx: SessionContext): string => idOrName) as ((ctx: SessionContext) => string) & object;
  const looksUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrName);
  const value = looksUuid ? { id: idOrName } : { name: idOrName };
  RESOLVER_DESCRIPTORS.set(fn, { kind: 'literal', value });
  return fn;
}

function makeNamedResolver(
  resolverId: 'principal.job_pool_id',
): (ctx: SessionContext) => string {
  const fn = ((ctx: SessionContext): string => {
    if (!ctx.principal.jobPoolId) {
      throw new Error(
        `resolver '${resolverId}' requires ctx.principal.jobPoolId; ` +
          `consuming service must populate it before startSession`,
      );
    }
    return ctx.principal.jobPoolId;
  }) as ((ctx: SessionContext) => string) & object;
  RESOLVER_DESCRIPTORS.set(fn, { kind: 'resolver', resolverId });
  return fn;
}

/**
 * Look up the serialised descriptor for an `JobPoolRef`. Returns
 * `undefined` if the value is not one of the substrate-shipped named
 * resolvers and is not a literal `{ id }` / `{ name }`.
 */
export function describeJobPoolRef(
  ref: JobPoolRef,
): SerialisedJobPoolRef | undefined {
  if (typeof ref === 'function') {
    return RESOLVER_DESCRIPTORS.get(ref as unknown as object);
  }
  if (typeof ref === 'object' && ref !== null) {
    if ('id' in ref && typeof ref.id === 'string') {
      return { kind: 'literal', value: { id: ref.id } };
    }
    if ('name' in ref && typeof ref.name === 'string') {
      return { kind: 'literal', value: { name: ref.name } };
    }
  }
  return undefined;
}
